import React, { Fragment, useState } from 'react';
import InputMask from 'react-input-mask';
import { connect } from 'react-redux';
import ReactMarkdown from 'markdown-to-jsx';
import { reduxForm, formValues, getFormSyncErrors, getFormMeta } from 'redux-form';
import BEMHelper from '@ynap/bem-helper';
import { SimpleAsyncButton } from '@ynap/button';
import Checkbox from '@ynap/checkbox';
import Field, { Input } from '@ynap/field';
import { submit, onSubmitFail } from '@ynap/form-utils';
import Notification, { Tooltip } from '@ynap/notification';
import PaymentCard from '@ynap/payment-card';
import collateErrors from './collateErrors';
import { buildLabels, handleExpiryDateMask, getCreditCardMask, getFormErrors, getCardTypeFromValidator } from './creditCardFormHelper';
import validate from './validate';
import type { CreditCardValues, CreditCardFormProps, CVVProps, InputState, CardDetails } from './@types';

const bem = new BEMHelper('CreditCardForm');

export function CVV(props: CVVProps) {
    const { className, initialValues, labels, showErrorNotification } = props;
    const isEditing = initialValues?.lastFourDigits;

    return (
        <Field
            autoComplete="cc-csc"
            className={className}
            component={Input}
            disabled={isEditing}
            id="cvvNumber"
            name="cvv"
            // eslint-disable-next-line no-return-assign
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.cvv)}
            // eslint-disable-next-line no-return-assign
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.cvvFormat || labels.cvv)}
            placeholder={isEditing ? '•••' : labels.cvv}
            showErrorNotification={showErrorNotification}
            type="number"
        />
    );
}

export function CreditCardForm(props: CreditCardFormProps) {
    const {
        cardTypeInfo,
        children,
        className,
        component: Component = 'form',
        config,
        error,
        form,
        formMeta,
        handleSubmit,
        hideSaveCard = false,
        initialValues,
        isCardWallet,
        loading,
        locale,
        messages,
        onSubmit,
        showCvvOnly,
        submitting = false,
        synchronousErrors,
        toolTipError,
    } = props;

    const cardExpiryMask = '99/9999';
    const [cardExpiry, setCardExpiry] = useState<InputState>({ value: '', mask: cardExpiryMask });
    const [creditCardNumber, setCreditCardNumber] = useState<InputState>({
        value: '',
        mask: getCreditCardMask(''),
    });
    const [cardDetails, setCardDetails] = useState<CardDetails>(cardTypeInfo || getCardTypeFromValidator(creditCardNumber.value));

    const acceptedCCTypes = config?.head?.acceptedCCTypes || [];
    const isCardWalletModifier = isCardWallet ? 'isCardWallet' : '';
    const isEditing = initialValues?.lastFourDigits;

    const onChangeCardNumber = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        setCreditCardNumber({
            value,
            mask: getCreditCardMask(value),
        });

        setTimeout(() => setCardDetails(cardTypeInfo || getCardTypeFromValidator(value)));
    };

    const onChangeCardExpiry = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
        setCardExpiry({
            value,
            mask: cardExpiryMask,
        });

    const renderForm = () => {
        const labels = buildLabels(messages, cardDetails);
        const firstError = getFormErrors(formMeta, synchronousErrors, isCardWallet);

        return !showCvvOnly ? (
            <Fragment>
                <div className={bem('cardDetails', isCardWalletModifier)}>{labels.cardDetails}</div>
                <Field
                    autoComplete="cc-name"
                    className={bem('cardholder', isCardWalletModifier)}
                    component={Input}
                    label={labels.cardHolderName}
                    name="cardHolderName"
                    placeholder={labels.cardHolderName}
                    showErrorNotification={false}
                    type="text"
                />
                <Field
                    autoComplete="cc-number"
                    className={bem('number')}
                    component={InputMask}
                    disabled={isEditing}
                    label={labels.number}
                    mask={creditCardNumber.mask}
                    maskChar={null}
                    name="number"
                    // eslint-disable-next-line no-return-assign
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.number)}
                    onChange={onChangeCardNumber}
                    // eslint-disable-next-line no-return-assign
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.cardNumberFormat || labels.number)}
                    placeholder={isEditing ? `•••• •••• •••• ${initialValues?.lastFourDigits}` : labels.number}
                    showErrorNotification={false}
                    value={creditCardNumber.value}
                />
                {!!cardDetails.cardType && (
                    <div className={bem('cardNumberContainer')}>
                        <PaymentCard
                            ariaLabel={cardDetails.cardType.toLowerCase().replace('-', ' ')}
                            as="div"
                            card={cardDetails.cardType.replace('-', '').toLowerCase()}
                            className={bem('cardType')}
                        />
                    </div>
                )}
                <div className={bem('cardAdditionalInfoContainer')}>
                    <Field
                        autoComplete="cc-exp"
                        beforeMaskedValueChange={handleExpiryDateMask}
                        className={bem('expiry', isCardWalletModifier)}
                        component={InputMask}
                        errorStyle="highlight"
                        mask={cardExpiry.mask}
                        maskChar={null}
                        maxLength={5}
                        name="expiryDateStr"
                        // eslint-disable-next-line no-return-assign
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.expiryDate)}
                        onChange={onChangeCardExpiry}
                        // eslint-disable-next-line no-return-assign
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.expiryDateFormat || labels.expiryDate)}
                        placeholder={labels.expiryDate}
                        showErrorNotification={false}
                        value={cardExpiry.value}
                    />
                    <CVV
                        initialValues={initialValues}
                        showErrorNotification={false}
                        labels={labels}
                        // If needed, you can pass cardDetails and creditCardNumber too.
                        className={bem('cvv', isCardWalletModifier)}
                    />
                </div>

                {firstError && <Notification className={bem('cardErrors')} message={firstError} type="field-error" />}
            </Fragment>
        ) : (
            <CVV
                showErrorNotification
                // Pass additional props if required.
                className={bem('cvv', ['confirmation', firstError?.includes('CVV') ? 'error' : ''])}
                labels={labels}
            />
        );
    };

    return (
        // @ts-ignore-error
        <Component className={bem(null, { submitting }, className)} id={form} onSubmit={onSubmit && handleSubmit && handleSubmit(submit)}>
            {renderForm()}

            {isCardWallet && locale && (
                <>
                    <p className={bem('privacyPolicy')}>
                        <ReactMarkdown
                            options={{
                                overrides: {
                                    a: (props) => (
                                        <a className={bem('privacyPolicyLink')} href={props.href} rel="noopener noreferrer" target="_blank">
                                            {props.children}
                                        </a>
                                    ),
                                },
                            }}
                        >
                            {messages.creditCardForm.privacyPolicy({ locale: locale.id })}
                        </ReactMarkdown>
                    </p>

                    <h4 className={bem('acceptedCardsTitle')}>{messages.creditCardForm.acceptedCards()}</h4>

                    <ul className={bem('acceptedCardsList')}>
                        {acceptedCCTypes.map((type) => (
                            <PaymentCard
                                key={type}
                                ariaLabel={type.replace('_', ' ').toLowerCase()}
                                card={type.replace('_', '').toLowerCase()}
                                className={bem('cardType', isCardWalletModifier)}
                            />
                        ))}
                    </ul>
                </>
            )}

            {!hideSaveCard && !showCvvOnly && (
                <Field
                    className={bem('saveCard')}
                    component={Checkbox}
                    format={(value: any) => value === 'true'}
                    name="saveCard"
                    normalize={(value: any) => value.toString()}
                >
                    {messages.creditCardForm.saveCard()}
                </Field>
            )}
            {typeof children === 'function' ? children(props) : children}
            {onSubmit && !showCvvOnly && (
                <Tooltip
                    key={String(submitting || error || toolTipError)}
                    className={bem('error', isCardWalletModifier)}
                    defaultOpen={!submitting && (!!error || !!toolTipError)}
                    message={error || (toolTipError && messages.creditCardForm.error?.(toolTipError)) || ''}
                    type="error"
                >
                    <SimpleAsyncButton
                        className={bem('submit')}
                        copy={messages.creditCardForm.submit ? messages.creditCardForm.submit() : ''}
                        loading={loading || submitting}
                        type="primary"
                    />
                </Tooltip>
            )}
        </Component>
    );
}

const mapStateToProps = (state: any, props: any) => ({
    synchronousErrors: getFormSyncErrors(props.isCardWallet ? 'creditCard' : 'paymentOptions')(state),
    formMeta: getFormMeta(props.isCardWallet ? 'creditCard' : 'paymentOptions')(state),
});

const CreditCardFormWithConnect = connect(mapStateToProps)(CreditCardForm);

export const CreditCardFormWithValues = formValues<{ numberValue: string }, any>({ numberValue: 'number' })(CreditCardFormWithConnect);

export default reduxForm<CreditCardValues, CreditCardFormProps>({
    form: 'creditCard',
    validate,
    // TODO collateErrors is not a valid type
    // @ts-ignore
    collateErrors,
    onSubmitFail,
    // @ts-ignore
})(CreditCardFormWithValues);
